import React, {useContext, useState} from 'react';
import glamorous from 'glamorous';
import {CHARCOAL, FOCUS_BLUE, TARMAC, WHITE} from '../../../shared/style/colors';
import {PHONE} from '../../../shared/style/breakpoints';
import SimpleGhostButton from '../../../shared/library/buttons/base/ghost';
import BackIcon from '../../../shared/library/icons/back-arrow-icon.svg';
import {TeamProfileContext} from '../enhancers/team-profile';
import {truncateText} from '../../../shared/utils/truncate-text';
import {BASE_TEXT, WEIGHT} from '../../../shared/style/typography';
import PropTypes from 'prop-types';
import MenuPopover from '../../../shared/library/popovers/menu';
import {EDIT_TEAM, SETTINGS_ICONS, SETTINGS_LABELS} from '../../../shared/constants/settings';
import {redirectTo} from '../../../shared/utils/navigation';
import {NAVBAR_HEIGHT} from '../../../shared/style/dimensions';

const Container = glamorous.div({
  gridArea: 'sidebar',
  background: WHITE,
  paddingLeft: 43,
  paddingRight: 43,
  marginLeft: -43,
  borderLeft: '1px solid #ebebeb',
  borderRight: '1px solid #ebebeb',
  position: 'relative',
  [PHONE]: {
    border: 0,
    paddingLeft: 0,
    paddingRight: 0,
    width: '100vw',
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
  }
});

const Content = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  top: '20px',
  [PHONE]: {
    minHeight: 'inherit',
    position: 'relative',
    paddingLeft: 10,
    paddingRight: 10,
    alignItems: 'center',
    paddingBottom: 0,
    paddingTop: 10
  }
});

const Sticky = glamorous.div({
  position: 'sticky',
  top: 20 + NAVBAR_HEIGHT,
  marginTop: 20,
  [PHONE]: {
    top: 0,
    marginTop: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
});

const Logo = glamorous.img({
  width: 115,
  height: 115,
  borderRadius: 4,
  marginBottom: 18,
  backgroundColor: WHITE,
  border: '1px solid #ebebeb',
  marginRight: 20
});

const Description = glamorous.div({
  ...BASE_TEXT,
  color: TARMAC,
  fontSize: 13,
  lineHeight: 1.5,
  margin: '22px 0',
  [PHONE]: {
    textAlign: 'center'
  }
});

const LogoContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center'
});

const CompanyName = glamorous.a({
  ...BASE_TEXT,
  display: 'table',
  color: TARMAC,
  fontSize: 18,
  lineHeight: 1.5,
  fontWeight: WEIGHT.BOLD,
  '> span': {
    color: FOCUS_BLUE
  }
});

const TeamName = glamorous.div({
  ...BASE_TEXT,
  color: CHARCOAL,
  fontSize: 25,
  lineHeight: 1.5,
  fontWeight: WEIGHT.BOLD
});

const SeeMore = glamorous.a({
  ...BASE_TEXT,
  color: CHARCOAL,
  fontSize: 13,
  textDecoration: 'underline'
});

export const GhostButton = glamorous(SimpleGhostButton)({
  height: 30,
  width: 180,
  margin: '0 0 20px 0',
  '> svg': {
    margin: '0 5px 0 0'
  },
  [PHONE]: {
    display: 'flex',
    width: '100%',
    padding: 0
  }
}).withComponent('a');

const Sidebar = ({team, adminOrMaintainer}) => {
  const [truncateDesc, setTruncateDesc] = useState(true);
  const teamProfileContext = useContext(TeamProfileContext);
  const settings = [EDIT_TEAM];
  const options = settings.map(setting => ({
    id: setting,
    label: SETTINGS_LABELS[setting],
    icon: SETTINGS_ICONS[setting]
  }));

  const handleMenuClick = option => {
    switch (option) {
      case EDIT_TEAM:
        redirectTo(`${team.canonicalUrl}/edit`);
        break;
    }
  };

  return (
    <Container>
      <Content>
        <Sticky>
          <GhostButton
            href={`/companies/${teamProfileContext.slug}`}
            title={teamProfileContext.slug}
            color={FOCUS_BLUE}
          >
            <BackIcon />
            Company Profile
          </GhostButton>
          <LogoContainer>
            {team.imageUrl ? (
              <Logo src={team.imageUrl} alt={team.name} />
            ) : (
              <Logo src="https://img.stackshare.io/empty-logo-square.png" alt={team.name} />
            )}
            {adminOrMaintainer && !team.githubId && (
              <MenuPopover options={options} onClick={handleMenuClick} />
            )}
          </LogoContainer>
          <CompanyName
            href={`/companies/${teamProfileContext.slug}`}
            title={teamProfileContext.slug}
          >
            {team.company.name}
            <span>/</span>
          </CompanyName>
          <TeamName>{team.name}</TeamName>
          {team.description && (
            <Description>
              {truncateDesc ? truncateText(team.description, 120, '... ') : team.description}
              {truncateDesc && team.description.length > 120 && (
                <SeeMore
                  href="#"
                  onClick={event => {
                    event.preventDefault();
                    setTruncateDesc(false);
                  }}
                >
                  more
                </SeeMore>
              )}
            </Description>
          )}
        </Sticky>
      </Content>
    </Container>
  );
};

Sidebar.propTypes = {
  team: PropTypes.object,
  adminOrMaintainer: PropTypes.bool
};

export default Sidebar;
